import { Card, CardBody } from "@components/ui/Card"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { DirectionSubjectRow } from "../types"

interface Props {
  rows: DirectionSubjectRow[]
  institutionAvg: number
}

function fillColor(avg: number, institutionAvg: number) {
  if (avg < institutionAvg - 0.4) return "bg-danger"
  if (avg < institutionAvg) return "bg-attention"
  return "bg-success"
}

export const SubjectComparisonChart = memo(function SubjectComparisonChart({
  rows,
  institutionAvg,
}: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()
  // The backend hands averages on a 0-10 canonical scale; the bar
  // widths normalize against that, the labels render in the
  // professor's active scale.
  const scaleMax = scale === "0-100" ? 100 : 10

  const computed = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        widthPct: Math.min(100, (row.avg_grade / scaleMax) * 100),
        delta: Math.round((row.avg_grade - institutionAvg) * 10) / 10,
      })),
    [rows, institutionAvg, scaleMax],
  )

  const refLinePct = Math.min(100, (institutionAvg / scaleMax) * 100)

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-sm font-semibold">{t("subjects.title")}</h2>
          <span className="text-ink-subtle text-xs">
            {t("subjects.reference", { value: formatGrade(institutionAvg, scale) })}
          </span>
        </div>

        {/* a11y table mirrors the bars */}
        <table className="sr-only">
          <caption>{t("subjects.aria_caption")}</caption>
          <thead>
            <tr>
              <th>{t("subjects.aria_subject")}</th>
              <th>{t("subjects.aria_avg")}</th>
              <th>{t("subjects.aria_classrooms")}</th>
            </tr>
          </thead>
          <tbody>
            {computed.map((row) => (
              <tr key={row.subject}>
                <td>{row.subject}</td>
                <td>{formatGrade(row.avg_grade, scale)}</td>
                <td>{row.classrooms}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <ul aria-hidden="true" className="space-y-2">
          {computed.map((row) => (
            <li key={row.subject} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="text-ink-muted font-medium">{row.subject}</span>
                <span className="text-ink-subtle">
                  {t("subjects.classrooms", { count: row.classrooms })}
                </span>
              </div>
              <div className="bg-inset relative h-3 w-full rounded-full">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
                    fillColor(row.avg_grade, institutionAvg),
                  )}
                  style={{ width: `${row.widthPct}%` }}
                />
                {/* Institution-average reference line. */}
                <div
                  className="bg-border-strong absolute top-0 bottom-0 w-px"
                  style={{ left: `${refLinePct}%` }}
                  aria-hidden="true"
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-ink font-semibold tabular-nums">
                  {formatGrade(row.avg_grade, scale)}
                </span>
                <span
                  className={cn("tabular-nums", row.delta < 0 ? "text-danger-ink" : "text-success")}
                >
                  {row.delta >= 0 ? "+" : "−"}
                  {formatGrade(Math.abs(row.delta), scale)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
})
